import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useStepsChartConfig, useStepsChartData, useStepsData } from "~/hooks/Steps";
import { StepsBreakdown } from "./StepsBreakdown";
import { StepsChart } from "./StepsChart";
import { StepsSummary } from "./StepsSummary";
import { TimeRangeSelector } from "../Shared";

type TimeRange = "1d" | "7d" | "1m" | "6m" | "1y";

interface StepsDetailViewProps {
  patientId: number;
  patientName: string;
}

export const StepsDetailView: React.FC<StepsDetailViewProps> = ({
  patientId,
  patientName,
}) => {
  const navigate = useNavigate();
  const [selectedRange, setSelectedRange] = useState<TimeRange>("7d");
  const [navigationOffset, setNavigationOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // Fetch steps data from API
  const {
    data: apiData,
    metadata,
    loading,
    error,
  } = useStepsData({
    patientId,
    timeRange: selectedRange,
    navigationOffset,
    selectedDate,
  });

  // Transform API data to chart format using custom hook
  const { chartData } = useStepsChartData({
    data: apiData,
    selectedRange,
    navigationOffset,
  });

  // Get chart configuration using custom hook
  const chartConfig = useStepsChartConfig({
    selectedRange,
    navigationOffset,
    selectedDate,
    lowChartData: chartData,
  });

  // Navigation functions
  const handlePrevious = () => {
    switch (selectedRange) {
      case "1d": {
        const prevDay = new Date(selectedDate);
        prevDay.setDate(selectedDate.getDate() - 1);

        setSelectedDate(prevDay);
        break;
      }
      case "7d": {
        const prevWeek = new Date(selectedDate);
        prevWeek.setDate(selectedDate.getDate() - 7);

        setSelectedDate(prevWeek);
        break;
      }
      case "1m": {
        const prevMonth = new Date(selectedDate);
        prevMonth.setMonth(selectedDate.getMonth() - 1);

        setSelectedDate(prevMonth);
        break;
      }
      case "6m": {
        const prev6Month = new Date(selectedDate);
        prev6Month.setMonth(selectedDate.getMonth() - 6);

        setSelectedDate(prev6Month);
        break;
      }
      case "1y": {
        const prevYear = new Date(selectedDate);
        prevYear.setFullYear(selectedDate.getFullYear() - 1);

        setSelectedDate(prevYear);
        break;
      }
    }
  };

  const handleNext = () => {
    switch (selectedRange) {
      case "1d": {
        const nextDay = new Date(selectedDate);
        nextDay.setDate(selectedDate.getDate() + 1);

        setSelectedDate(nextDay);
        break;
      }
      case "7d": {
        const nextWeek = new Date(selectedDate);
        nextWeek.setDate(selectedDate.getDate() + 7);

        setSelectedDate(nextWeek);
        break;
      }
      case "1m": {
        const nextMonth = new Date(selectedDate);
        nextMonth.setMonth(selectedDate.getMonth() + 1);

        setSelectedDate(nextMonth);
        break;
      }
      case "6m": {
        const next6Month = new Date(selectedDate);
        next6Month.setMonth(selectedDate.getMonth() + 6);

        setSelectedDate(next6Month);
        break;
      }
      case "1y": {
        const nextYear = new Date(selectedDate);
        nextYear.setFullYear(selectedDate.getFullYear() + 1);

        setSelectedDate(nextYear);
        break;
      }
    }
  };

  // Handle time range change and preserve current view
  const handleTimeRangeChange = (newRange: TimeRange) => {
    setSelectedRange(newRange);
    setNavigationOffset(0); // Reset navigation offset

    // Don't reset selectedDate - keep the current month/year being viewed
    // This allows users to navigate between months/years without losing their place
  };

  // Date selection handler
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    // Reset navigation offset since we're now using selectedDate directly
    setNavigationOffset(0);
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between border-b border-gray-700 pb-4">
        <button
          onClick={() => navigate(-1)}
          className="text-lg font-medium text-blue-400 transition-colors hover:text-blue-300"
        >
          ← Back
        </button>
        <h1 className="text-2xl font-semibold text-white">
          Steps - {patientName}
        </h1>
        <div className="w-16"></div> {/* Spacer for centering */}
      </div>

      {/* Content */}
      <div className="space-y-6">
        {/* Time Range Selector */}
        <TimeRangeSelector
          selectedRange={selectedRange}
          setSelectedRange={handleTimeRangeChange}
        />

        {/* Summary Statistics */}
        <StepsSummary
          filteredData={chartData}
          metadata={metadata}
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          onDateSelect={handleDateSelect}
          selectedDate={selectedDate}
          selectedRange={selectedRange}
        />

        {/* Chart */}
        <div className="mx-auto w-4/5 rounded-xl bg-gray-900/50 p-4">
          <StepsChart
            key={`${selectedRange}-${selectedDate.getTime()}-${chartData.length}`}
            selectedRange={selectedRange}
            navigationOffset={navigationOffset}
            selectedDate={selectedDate}
            chartData={chartData}
            chartConfig={chartConfig}
          />
        </div>

        {/* Breakdown List */}
        <StepsBreakdown
          selectedRange={selectedRange}
          filteredData={chartData}
          breakdownTitle="Hourly Breakdown"
          loading={loading}
          error={error}
        />
      </div>
    </div>
  );
};
